import { useUIv2 } from '@/hooks/useUIv2'
import { isAnnotatorRole } from '@/types/users'
import DataCatalogV2 from './DataCatalogV2'
import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  ChangeEvent,
  MouseEvent,
  DragEvent,
} from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Upload,
  Search,
  Download,
  RefreshCw,
} from 'lucide-react'
import { colors } from '@/styles/colors'
import { useAppStore } from '@/store'
import {
  useDocumentStatusStream,
  type DocumentStatusEvent,
} from '@/hooks/useDocumentStatusStream'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import PageLayout from '@/components/layout/PageLayout'
import type { DocumentRecord } from '@/types/catalog'
import { CatalogTable } from './CatalogTable'
import { canMatchEntitySelection } from './matchEntityEligibility'
import { MODAL_TYPE } from '@/store/modalSlice'
import { DOC_TYPES, RowAction } from '@/components/DataTable/DataTableTypes'
import {
  Trash2Icon,
  Pen,
  Pencil,
  Link2,
  Unlink2,
  MonitorPlay,
} from 'lucide-react'
import { toast } from 'sonner'
import { useExplainAsVideoEnabled, canExplainAsVideo } from '@/features/videoBrief/explainAsVideo'
import DocumentPreviewModal from './DocumentPreviewModal'
import { invalidateDocumentCache } from '@/routes/IntelligentBoard/DocumentPreviewShape/DocumentPreview'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  TooltipProvider,
} from '@/components/ui/tooltip'

import { useTranslation } from '@/i18n'
import { useApiFeatureFlag } from '@/hooks/useFeatureFlag'
import { apiService } from '@/service/api'
import { mapEventTypeToStatus } from '@/utils/documentStatus'
import {
  useSessionState,
  DATA_CATALOG_STORAGE_KEYS,
} from '@/hooks/useSessionState'
import { trackEvent } from '@/utils/analytics'
import { GroupNameDialog } from './GroupNameDialog'
import { ManageGroupFilesModal } from './ManageGroupFilesModal'
import { ReviewGroupModal } from './ReviewGroupModal'
import Paginator from '@/components/Paginator'
import { useFeatureEnabled } from '@/features/featureAccess/useFeatureAccess'

// Default sorting configuration
const DEFAULT_SORT = [{ id: 'created_at', desc: true }]
// UI-65: no "P&ID Set" option — sets are detected automatically now.
const FILTER_TYPES = [
  'all',
  ...Object.values(DOC_TYPES).filter(
    (type) => type !== 'interview' && type !== 'graph' && type !== 'datasheet'
  ),
] // Interview, graph, and datasheet are not part of MVP

// Valid page sizes (must match Paginator component)
const VALID_PAGE_SIZES = [10, 20, 50, 100]

const DataCatalog = () => {
  const isV2 = useUIv2()
  if (isV2) return <DataCatalogV2 />
  return <DataCatalogLegacy />
}

const DataCatalogLegacy = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const getDocTypeLabel = (type: string) => {
    return t(`data_catalog:doc_types.${type}` as never)
  }

  const {
    documents,
    totalDocuments,
    fetchDocuments,
    updateDocument,
    deleteDocuments,
    openModal,
    setModalData,
    setUploadCallbacks,
    loading,
    user,
    organizationUsers,
    fetchOrganizationUsers,
  } = useAppStore()

  const isInternalUser =
    (user?.email?.toLowerCase().endsWith('@intuigence.ai') ||
      user?.email?.toLowerCase().endsWith('@intuigence.com')) ??
    false

  const { enabled: restrictUpload, loading: restrictUploadLoading } =
    useApiFeatureFlag('restrict_upload_to_internal_users', false)
  // "Explain as video" row action (PRD: Explain as video); the server gates the request too.
  const explainVideoEnabled = useExplainAsVideoEnabled()

  // Upload is enabled when: flag is off (all users allowed) OR user is internal.
  // Annotators are read-only outside the P&ID editor — never allow upload.
  const isUploadEnabled =
    !isAnnotatorRole(user?.role) &&
    (restrictUploadLoading ? isInternalUser : !restrictUpload || isInternalUser)

  // Initialize state from sessionStorage for persistence across navigation
  const [selectedType, setSelectedType] = useSessionState(
    DATA_CATALOG_STORAGE_KEYS.type,
    'all',
    (v) => typeof v === 'string' && FILTER_TYPES.includes(v)
  )
  const [selectedUploader, setSelectedUploader] = useSessionState(
    DATA_CATALOG_STORAGE_KEYS.uploader,
    'all',
    (v) => typeof v === 'string'
  )
  const [tablePage, setTablePage] = useState<DocumentRecord[]>([])
  const [groupedPageEntries, setGroupedPageEntries] = useState<
    Array<{ type: 'group'; data: DocumentRecord[]; setId: string }>
  >([])
  const [totalPages, setTotalPages] = useState(1)
  const [searchTerm, setSearchTerm] = useSessionState(
    DATA_CATALOG_STORAGE_KEYS.search,
    '',
    (v) => typeof v === 'string'
  )
  const [sortBy, setSortBy] = useSessionState(
    DATA_CATALOG_STORAGE_KEYS.sortBy,
    'created_at',
    (v) => typeof v === 'string'
  )
  const [sortDir, setSortDir] = useSessionState<'asc' | 'desc'>(
    DATA_CATALOG_STORAGE_KEYS.sortDir,
    'desc',
    (v) => v === 'asc' || v === 'desc'
  )
  // Derive sorting array from individual values for compatibility with DataTable
  const sorting = useMemo(
    () => (sortBy ? [{ id: sortBy, desc: sortDir === 'desc' }] : DEFAULT_SORT),
    [sortBy, sortDir]
  )
  const [page, setPage] = useSessionState(
    DATA_CATALOG_STORAGE_KEYS.page,
    1,
    (v) => typeof v === 'number' && v >= 1
  )
  const [pageSize, setPageSize] = useSessionState(
    DATA_CATALOG_STORAGE_KEYS.pageSize,
    10,
    (v) => typeof v === 'number' && VALID_PAGE_SIZES.includes(v as number)
  )
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm)
  const [uploadingDocuments, setUploadingDocuments] = useState<
    DocumentRecord[]
  >([])
  // Ref for synchronous access to abort controllers (avoids race condition in single file uploads)
  const uploadAbortControllersRef = useRef<Map<string, AbortController>>(
    new Map()
  )
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set())
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)
  const [showGroupDialog, setShowGroupDialog] = useState(false)
  const [pendingGroupName, setPendingGroupName] = useState('')
  const [showReviewGroupModal, setShowReviewGroupModal] = useState(false)
  const [showManageFilesModal, setShowManageFilesModal] = useState(false)
  const [manageFilesSetId, setManageFilesSetId] = useState<string>('')
  const [manageFilesDocs, setManageFilesDocs] = useState<DocumentRecord[]>([])
  const [previewDocument, setPreviewDocument] = useState<DocumentRecord | null>(
    null
  )
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  const userIdToNameMap = useMemo(() => {
    const mapping = new Map<string, string>()
    organizationUsers.forEach((user) => {
      if (user.user_id && user.name) {
        mapping.set(user.user_id, user.name)
      } else if (user.user_id && user.email) {
        mapping.set(user.user_id, user.email)
      }
    })
    return mapping
  }, [organizationUsers])
  // Ref to track filename -> docId mapping for upload callbacks
  const uploadFileToDocIdRef = useRef<Map<string, string>>(new Map())
  // Ref to track document completion state to avoid race conditions with stale state
  const documentCompletionRef = useRef<
    Map<string, { indexing: boolean; embedding: boolean }>
  >(new Map())
  // Ref to access current documents without stale closure
  const documentsRef = useRef<DocumentRecord[]>(documents)
  // Ref to access current uploading documents without stale closure
  const uploadingDocumentsRef = useRef<DocumentRecord[]>(uploadingDocuments)
  // Batch tracking for decreasing upload timestamps (so DESC sort preserves selection order)
  const uploadBatchBase = useRef(0)
  const uploadBatchOffset = useRef(0)
  const lastUploadAddTime = useRef(0)

  // FS-489 — Upload-batch backstop counter
  // ---------------------------------------
  // Two cleanup paths coexist for `uploadingDocuments`:
  //
  //   1. **Per-upload heuristic** (`realDocMatchesTemp` cleanup useEffect
  //      below). Removes a temp the moment its matching real row appears
  //      in the *currently-viewed server page*, matched by name + ±5s
  //      timestamp. Fast and granular when it fires.
  //
  //   2. **Batch-end backstop** (this counter + `releaseUploadSlot` +
  //      blanket-clear). Increments once per `onStart`, decrements once
  //      per `onComplete`. When the counter drops to 0, the whole
  //      `uploadingDocuments` list is cleared and one final
  //      `fetchDocuments()` fires — the server's paginated view becomes
  //      the source of truth from that point.
  //
  // Why both: the per-upload heuristic misses real edge cases — extension
  // case (`.PDF` vs `.pdf`), filenames that already contain `(N)` before
  // the backend auto-rename adds another, clock skew across batched
  // uploads, and (the worst one) real rows landing on a server page the
  // user isn't currently viewing. Pegah's 48-file repro hit the last:
  // 48 uploads + page_size=10 → 38 of the 48 real rows on server pages
  // 2–5 → 38 temps stuck on page 1 forever (until reload). The backstop
  // fires exactly once per batch-end and guarantees convergence.
  //
  // Decrement ownership: `releaseUploadSlot` is called from `onComplete`
  // ONLY. The four upload wrappers (`modalSlice.ts:117-122`,
  // `ModalProvider.tsx:127-131` and `:262-264`,
  // `UnifiedUploadModal.tsx:223-227`) all fire `onComplete` in a
  // `finally` block, so success AND abort paths both retire the counter
  // slot exactly once. The cancel handlers (`handleDeleteDoc`,
  // `handleBulkDelete`) deliberately do not call `releaseUploadSlot` —
  // doing so would double-decrement once the aborted upload's wrapper
  // `finally` runs.
  const uploadInFlightRef = useRef(0)

  // Keep documentsRef in sync with documents state
  useEffect(() => {
    documentsRef.current = documents
  }, [documents])

  // Keep uploadingDocumentsRef in sync with uploadingDocuments state
  useEffect(() => {
    uploadingDocumentsRef.current = uploadingDocuments
  }, [uploadingDocuments])

  // FS-489: decrement the in-flight counter and, if it has drained to 0,
  // run the batch-end backstop (clear residual temps + refresh server
  // view). Called from `onComplete` only — see `uploadInFlightRef`
  // docblock above for why the cancel handlers deliberately do not.
  //
  // The clamp-at-0 early return is defense-in-depth: if a future change
  // ever introduces an unmatched decrement (or React replays an effect
  // unexpectedly), the counter floors at 0 rather than going negative
  // and re-firing the backstop every subsequent call.
  const releaseUploadSlot = useCallback(() => {
    const prev = uploadInFlightRef.current
    if (prev <= 0) {
      uploadInFlightRef.current = 0
      return
    }
    const next = prev - 1
    uploadInFlightRef.current = next
    if (next === 0) {
      // Fetch first so the server's view of the real (possibly auto-renamed)
      // row lands in `documents` before we drop the temp. The per-upload
      // cleanup useEffect then retires the temp via `realDocMatchesTemp` the
      // moment the matching real row arrives. Clearing residuals in
      // `.finally` covers temps whose real row landed on a server page the
      // user isn't viewing (the original FS-489 scenario).
      //
      // Order matters: doing `setUploadingDocuments([])` synchronously before
      // the fetch resolved created a ~100-500ms gap where the temp was gone
      // but the real row hadn't arrived yet — the disappear/reappear symptom
      // for single-file auto-rename uploads.
      fetchDocuments().finally(() => setUploadingDocuments([]))
    }
  }, [fetchDocuments])

  // FS-498 — stale-temp sweeper.
  // ---------------------------
  // Third safety net for stuck temp docs. The first two are:
  //   1. `onComplete` (per-upload) — transitions status and decrements
  //      the in-flight counter. If counter→0, the FS-489 backstop
  //      wipes residual temps + fetchDocuments.
  //   2. cleanup useEffect (per-real-doc match) — retires a temp the
  //      moment its matching real row appears on the current page.
  //
  // Both rely on either `onComplete` firing or the matching real doc
  // landing on the currently-viewed server page. Pegah's FS-498 hits
  // the gap where both fail simultaneously — a fetch that never
  // settles (proxy buffering / browser quirk / connection holdover)
  // leaves `onComplete` unfired, and the real row landed on a
  // server page she isn't viewing. The temp sits at "Uploading"
  // until she manually refreshes.
  //
  // This sweeper closes the gap: every 30s while temps exist, scan
  // for any temp older than 2 minutes — well past the p99 upload
  // duration (~90s observed on dev) — and force the recovery the
  // user would otherwise have to do by hand for those specific
  // temps.
  //
  // Targeted-retire contract (greptile-apps[bot] review on
  // PR #13945): only the temps that crossed the threshold get
  // dropped, and `uploadInFlightRef` shrinks by exactly that
  // count. A blanket `setUploadingDocuments([])` + `counter = 0`
  // wipe — what an earlier draft did — also tears down temps that
  // are still legitimately in-flight. Their later `onComplete`
  // would land on the `prev <= 0` clamp in `releaseUploadSlot`,
  // skip the `fetchDocuments()` that surfaces their real row, and
  // those completed uploads would stay invisible until a manual
  // refresh. Shrinking the counter by `stuck.length` keeps the
  // FS-489 batch-end backstop arming correctly for the surviving
  // uploads.
  const hasInFlightUploads = uploadingDocuments.length > 0
  useEffect(() => {
    if (!hasInFlightUploads) return
    const STALE_THRESHOLD_MS = 120_000
    const SWEEP_INTERVAL_MS = 30_000

    const interval = setInterval(() => {
      const temps = uploadingDocumentsRef.current
      if (temps.length === 0) return
      const now = Date.now()
      const stuck = temps.filter((temp) => {
        const tempTime = new Date(temp.created_at).getTime()
        return now - tempTime > STALE_THRESHOLD_MS
      })
      if (stuck.length === 0) return
      console.warn(
        `[FS-498] stale-temp sweep: ${stuck.length}/${temps.length} temp(s) past ${STALE_THRESHOLD_MS}ms — targeted retire`,
        stuck.map((t) => t.name),
      )
      const stuckIds = new Set(stuck.map((t) => t.id))
      setUploadingDocuments((prev) =>
        prev.filter((t) => !stuckIds.has(t.id)),
      )
      uploadInFlightRef.current = Math.max(
        0,
        uploadInFlightRef.current - stuck.length,
      )
      fetchDocuments()
    }, SWEEP_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [hasInFlightUploads, fetchDocuments])

  // Returns true if two docs refer to the same file by name (exact or backend auto-rename e.g. "file (1).xlsx")
  const sameFileName = useCallback(
    (realName: string, tempName: string): boolean => {
      if (realName === tempName) return true
      const lastDot = tempName.lastIndexOf('.')
      const tempBase = lastDot >= 0 ? tempName.slice(0, lastDot) : tempName
      const tempExt = lastDot >= 0 ? tempName.slice(lastDot) : ''
      const prefix = tempBase + ' ('
      const suffix = ')' + tempExt
      if (!realName.startsWith(prefix) || !realName.endsWith(suffix))
        return false
      const middle = realName.slice(
        prefix.length,
        realName.length - suffix.length
      )
      return /^\d+$/.test(middle)
    },
    []
  )

  // Returns true if a real doc is the same file as a temp doc (so we can remove the temp row).
  // Backend converts xlsx/csv uploaded as "document" to "datasheet" in the same record, so treat document+datasheet as same upload when names match.
  // IMPORTANT: The real doc must have come from THIS upload, not a pre-existing
  // file with the same name. Otherwise the old real doc replaces the uploading
  // temp doc causing disappear/reappear (the FS-38 regression).
  //
  // FS-498: prefer `upload_started_at` over `created_at` as the temporal
  // discriminator. `upload_started_at` is generated by the frontend in
  // `onStart` and forwarded to the backend via the upload payload, so the
  // temp doc and the real doc carry the SAME value for the same upload —
  // immune to client/server clock skew. `created_at` is two independent
  // wall-clock readings (browser at onStart vs server at row INSERT) and
  // the previous `realTime < tempTime - 5000` guard rejected legitimate
  // matches whenever those wall-clocks drifted by more than 5s, leaving
  // the temp stuck on "Uploading" until the user refreshed.
  const realDocMatchesTemp = useCallback(
    (
      real: { name: string; type: string; created_at?: string; upload_started_at?: string | null },
      temp: { name: string; type: string; created_at?: string; upload_started_at?: string | null }
    ): boolean => {
      const isExactName = real.name === temp.name
      const isAutoRenamed = !isExactName && sameFileName(real.name, temp.name)

      if (!isExactName && !isAutoRenamed) return false

      // Primary discriminator: `upload_started_at` is set by the frontend
      // in `onStart` and round-tripped through the server, so a real doc
      // born from THIS upload carries the same value as the temp. Any old
      // real doc with a different `upload_started_at` is from a prior
      // upload — reject the match.
      if (temp.upload_started_at && real.upload_started_at) {
        if (real.upload_started_at !== temp.upload_started_at) return false
      } else if (real.created_at && temp.created_at) {
        // Legacy fallback for rows that predate `upload_started_at`
        // (column is nullable). Keep the 5s grace window for these.
        const realTime = new Date(real.created_at).getTime()
        const tempTime = new Date(temp.created_at).getTime()
        if (realTime < tempTime - 5000) return false
      }

      if (real.type === temp.type) return true
      // Backend converts document -> datasheet for spreadsheets (xlsx/csv)
      const docTypes = new Set([real.type, temp.type])
      if (docTypes.has('document') && docTypes.has('datasheet')) return true
      if (docTypes.has('timeseries') && docTypes.has('datasheet')) return true
      return false
    },
    [sameFileName]
  )

  // Clean up temp documents when a real document with the same name AND type appears
  // (same file can exist as both document and timeseries, so match by name+type)
  // Also remove temp when backend returns an auto-renamed name (e.g. "file (1).xlsx") so we don't show two rows
  useEffect(() => {
    if (uploadingDocuments.length === 0 || documents.length === 0) {
      return
    }

    const tempDocsToRemove = uploadingDocuments.filter(
      (tempDoc: DocumentRecord) =>
        documents.some((realDoc: DocumentRecord) =>
          realDocMatchesTemp(realDoc, tempDoc)
        )
    )

    if (tempDocsToRemove.length > 0) {
      const timer = setTimeout(() => {
        setUploadingDocuments((prev: DocumentRecord[]) =>
          prev.filter(
            (tempDoc: DocumentRecord) =>
              !documents.some((realDoc: DocumentRecord) =>
                realDocMatchesTemp(realDoc, tempDoc)
              )
          )
        )
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [documents, uploadingDocuments, realDocMatchesTemp])

  // Ref for debouncing search updates
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null)

  const CATALOG_COLS = [
    { id: 'name', label: t('data_catalog:file_name') },
    { id: 'type', label: t('data_catalog:type') },
    { id: 'file_size', label: t('data_catalog:file_size') },
    { id: 'status', label: t('common:table_columns.status') },
    { id: 'uploaded_by', label: t('data_catalog:uploaded_by') },
    // NOTE: 'created_at' is the column ID but it displays upload_started_at (see CatalogTable.tsx and sort mapping below)
    { id: 'created_at', label: t('data_catalog:date_created') },
  ]

  // Helper function to check if a document ID is a temporary uploading document
  const isTempDocument = useCallback(
    (docId: string): boolean => {
      return uploadingDocuments.some((doc) => doc.id === docId)
    },
    [uploadingDocuments]
  )

  // Helper function to create temporary document entries for uploading files
  const createTempDocument = (
    fileName: string,
    type: DocumentRecord['type']
  ): DocumentRecord => ({
    id: crypto.randomUUID(),
    name: fileName,
    description: '',
    created_at: new Date().toISOString(),
    user_id: user?.userId || '',
    uploader_name: user?.name || user?.email || null,
    tenant_id: user?.tenant_id || '',
    type,
    is_indexed: false,
    location: null,
    status: 'uploading',
    properties: {
      pnid: undefined,
      row_count: undefined,
      graph_id: undefined,
    },
  })

  // Determine if we need SSE based on document statuses
  const hasProcessingDocuments = useMemo(() => {
    // A just-uploaded file exists ONLY as a temp row until the server doc is
    // fetched, and temp rows are component state, not `documents`. Deriving the
    // gate from `documents` alone meant a catalog page whose fetched rows were
    // all success/failed disabled the stream outright, so the row the user was
    // watching received no events at all — the parse can run for the better
    // part of an hour before anything else would refetch.
    if (uploadingDocuments.length > 0) {
      return true
    }

    if (!documents || documents.length === 0) {
      return false // No documents, no need for SSE
    }

    // Check if any document is in a processing state (not success/failed)
    return documents.some((doc) => {
      const status = doc.status?.toLowerCase() || ''
      const isIndexed = doc.is_indexed

      if (doc.type === 'document') {
        // Legacy documents: if status='success' but no processing_metadata, consider complete
        if (status === 'success' || status === 'failed') {
          return false // Don't keep SSE alive for legacy completed docs
        }

        // For type='document', check if all required stages are complete
        const allComplete =
          doc.processing_metadata?.completed_indexing === true &&
          doc.processing_metadata?.completed_embedding === true

        // Keep SSE alive if NOT all complete
        return !allComplete || (status !== 'success' && status !== 'failed')
      } else if (doc.type === 'device_config') {
        // Legacy documents: if status='success' but no processing_metadata, consider complete
        if (status === 'success' || status === 'failed') {
          return false // Don't keep SSE alive for legacy completed docs
        }

        const allComplete =
          doc.processing_metadata?.completed_indexing === true &&
          doc.processing_metadata?.completed_embedding === true

        // Keep SSE alive if NOT all complete
        return !allComplete || (status !== 'success' && status !== 'failed')
      } else {
        // For other types, use simple status check
        return (
          !isIndexed ||
          (status !== '' && status !== 'success' && status !== 'failed')
        )
      }
    })
  }, [documents, uploadingDocuments])

  // Apply an SSE status patch to the row WHEREVER it currently lives.
  //
  // `updateDocument` writes only to the store's `documents`. A just-uploaded
  // file is displayed from `uploadingDocuments` (component state) until the
  // server row is fetched and matched, and FS-499 gives the temp row the SAME
  // server-issued id — so events for it hit `documents.find(...) === undefined`
  // and the patch was dropped silently, leaving the row on its optimistic
  // status while the backend had already moved on.
  const applyStatusPatch = useCallback(
    (documentId: string, documentName: string | undefined, patch: Partial<DocumentRecord>) => {
      updateDocument(documentId, patch)
      setUploadingDocuments((prev) => {
        // Resolve the event to exactly ONE temp row, or none.
        //
        // Id first: only the pre-registered path (FS-499) gives the temp row the
        // server docId, and when it does the match is unambiguous. Region
        // selection, multi-page and the register-pending failure fallback all
        // leave a client-generated UUID that an event id can never equal, so
        // those rows can only be found by name.
        //
        // Name is NOT unique, though — the uploader registers a whole batch
        // before uploading and does not reject duplicate filenames. Patching
        // every name match would let one document's failure mark its
        // same-named siblings failed too, so an ambiguous name resolves to
        // NOTHING. A row left on its optimistic status is recoverable (the
        // FS-498 sweeper and the next fetch both correct it); a row showing
        // another document's failure is not.
        const byId = prev.findIndex((doc) => doc.id === documentId)
        let target = byId
        if (target < 0 && documentName) {
          // Exact name beats auto-rename equivalence: with temps "f.pdf" and
          // "f (1).pdf" present, an event for "f (1).pdf" is exact on one and
          // rename-equivalent to both, so preferring exact keeps it decidable.
          const exact = prev.filter((doc) => doc.name === documentName)
          const candidates =
            exact.length > 0
              ? exact
              : prev.filter((doc) => sameFileName(documentName, doc.name))
          if (candidates.length === 1) {
            target = prev.indexOf(candidates[0])
          }
        }
        if (target < 0) return prev
        return prev.map((doc, i) => (i === target ? { ...doc, ...patch } : doc))
      })
    },
    [updateDocument, sameFileName],
  )

  // Subscribe to real-time document status updates
  // onDisconnected callback is called when SSE connection fails after max retries
  // This enables fallback polling to keep document list in sync
  useDocumentStatusStream({
    enabled: hasProcessingDocuments,
    onDisconnected: useCallback(() => {
      // Refresh documents when SSE connection is lost to ensure data is up-to-date
      // This is triggered by SSE fallback polling (every 30s when disconnected)
      console.log('SSE disconnected - refreshing documents')
      fetchDocuments()
    }, [fetchDocuments]),
    onStatusUpdate: useCallback(
      (event: DocumentStatusEvent) => {
        console.log(
          '📡 SSE Event:',
          event.event_type,
          'for',
          event.document_name
        )

        // Find document to determine type - check both documents and uploadingDocuments
        // to handle events that arrive before the document is in the main documents array
        // Use refs to avoid stale closure and prevent callback recreation on every state update
        const doc =
          documentsRef.current?.find((d) => d.id === event.document_id) ||
          uploadingDocumentsRef.current?.find((d) => d.id === event.document_id)
        const mappedStatus = mapEventTypeToStatus(event.event_type)

        // KG regeneration is best effort after ingestion. Read its persisted
        // result instead of overriding the backend's success/failure guards.
        if (event.event_type.startsWith('kg_regen_')) {
          fetchDocuments()
          return
        }

        // Handle failure events first - applies to all document types
        if (
          event.event_type === 'indexing_failed' ||
          event.event_type === 'embedding_failed'
        ) {
          // Clean up completion ref on failure
          documentCompletionRef.current.delete(event.document_id)
          trackEvent('document-processing_failed', {
            document_id: event.document_id,
            document_type: doc?.type || 'unknown',
            error_type: event.event_type,
          })
          applyStatusPatch(event.document_id, event.document_name, {
            status: 'failed',
            // Preserve existing is_indexed=true if document was already indexed
            // (failure in one workflow shouldn't undo successful indexing)
            is_indexed: doc?.is_indexed || event.is_indexed,
          })
          return // Don't process completion tracking for failed documents
        }

        // Handle re-processing: reset completion state when _started events arrive
        if (event.event_type === 'indexing_started') {
          // Clear existing completion state for fresh re-processing
          documentCompletionRef.current.delete(event.document_id)
        }

        if (doc?.type === 'document') {
          // Use ref to track completion state to avoid race conditions with stale state
          // This ensures we never regress from 'success' to 'processing'
          let completion = documentCompletionRef.current.get(event.document_id)
          if (!completion) {
            // Initialize from existing metadata (if any) or defaults
            completion = {
              indexing: doc.processing_metadata?.completed_indexing === true,
              embedding: doc.processing_metadata?.completed_embedding === true,
            }
            documentCompletionRef.current.set(event.document_id, completion)
          }

          // Update completion flags based on event type (only set to true, never false)
          if (event.event_type === 'indexing_completed') {
            completion.indexing = true
          } else if (event.event_type === 'embedding_completed') {
            completion.embedding = true
            // Fallback: Backend saves status='success' to DB after embedding completes
            // trust the backend's DB status by triggering a refetch
            setTimeout(() => fetchDocuments(), 1000)
          } else if (event.event_type === 'processing_complete') {
            // Mark all as complete
            completion.indexing = true
            completion.embedding = true
          }

          // Check that both text processing stages are done
          const allComplete =
            completion.indexing && completion.embedding

          // Build metadata from ref state (source of truth)
          const metadata = {
            completed_indexing: completion.indexing,
            completed_embedding: completion.embedding,
          }

          // Update document with completion metadata
          // Only set 'success' when all complete, otherwise keep 'processing'
          // IMPORTANT: is_indexed is monotonic - only update if going from false to true
          // This prevents race conditions where late-arriving events (like indexing_completed)
          // overwrite is_indexed=true with false
          applyStatusPatch(event.document_id, event.document_name, {
            status: allComplete ? 'success' : 'processing',
            is_indexed: doc?.is_indexed || event.is_indexed,
            processing_metadata: metadata,
          })

          // Memory cleanup: remove completed entries from ref after a delay
          if (allComplete) {
            trackEvent('document-processing_completed', {
              document_id: event.document_id,
              document_type: doc?.type || 'document',
            })
            setTimeout(() => {
              documentCompletionRef.current.delete(event.document_id)
            }, 5000)
            // Auto-refetch to get complete server-side metadata
            setTimeout(() => fetchDocuments(), 100)
          }
        } else if (doc?.type === 'device_config') {
          // Use ref to track completion state to avoid race conditions
          let completion = documentCompletionRef.current.get(event.document_id)
          if (!completion) {
            // Initialize from existing metadata (if any) or defaults
            completion = {
              indexing: doc.processing_metadata?.completed_indexing === true,
              embedding: doc.processing_metadata?.completed_embedding === true,
            }
            documentCompletionRef.current.set(event.document_id, completion)
          }

          // Update completion flags based on event type (only set to true, never false)
          if (event.event_type === 'indexing_completed') {
            completion.indexing = true
          } else if (event.event_type === 'embedding_completed') {
            completion.embedding = true
          } else if (event.event_type === 'processing_complete') {
            // Mark all as complete
            completion.indexing = true
            completion.embedding = true
          }

          const allComplete = completion.indexing && completion.embedding

          // Build metadata from ref state (source of truth)
          const metadata = {
            completed_indexing: completion.indexing,
            completed_embedding: completion.embedding,
          }

          // Update document with completion metadata
          // Only set 'success' when all complete, otherwise keep 'processing'
          // IMPORTANT: is_indexed is monotonic - only update if going from false to true
          applyStatusPatch(event.document_id, event.document_name, {
            status: allComplete ? 'success' : 'processing',
            is_indexed: doc?.is_indexed || event.is_indexed,
            processing_metadata: metadata,
          })

          // Memory cleanup: remove completed entries from ref after a delay
          if (allComplete) {
            trackEvent('document-processing_completed', {
              document_id: event.document_id,
              document_type: doc?.type || 'device_config',
            })
            setTimeout(() => {
              documentCompletionRef.current.delete(event.document_id)
            }, 5000)
            // Auto-refetch to get complete server-side metadata
            setTimeout(() => fetchDocuments(), 100)
          }
        } else {
          // For other types (pnid, datasheet, timeseries), direct status update
          // IMPORTANT: is_indexed is monotonic - only update if going from false to true
          const update: Partial<DocumentRecord> = {
            status: mappedStatus || event.event_type,
            is_indexed: doc?.is_indexed || event.is_indexed,
          }

          // Store error message for failed events
          if (event.event_type.endsWith('_failed') && event.metadata?.error) {
            update.error_message = event.metadata.error
          }

          applyStatusPatch(event.document_id, event.document_name, update)
        }

        // Only refetch when processing completes and creates new server-side data
        // (e.g., graph_id for P&ID/PFD, row_count for datasheets)
        // Don't refetch on simple status updates - SSE already updates those via updateDocument()
        if (
          event.event_type === 'pnid_processing_completed' ||
          event.event_type === 'pfd_processing_completed' ||
          event.event_type === 'datasheet_processing_completed' ||
          event.event_type === 'timeseries_processing_completed'
        ) {
          // Small delay to ensure DB updates complete before refetch
          setTimeout(() => fetchDocuments(), 100)
        }
      },
      // IMPORTANT: Do not add documents/uploadingDocuments to deps!
      // They are accessed via refs to prevent callback recreation on every state update,
      // which would cause SSE subscription churn and missed events.
      // `applyStatusPatch` is safe: it only closes over `updateDocument` and the
      // `setUploadingDocuments` setter (both stable), never the row arrays.
      [applyStatusPatch, fetchDocuments]
    ),
  })

  // Set up upload tracking callbacks for single file uploads
  useEffect(() => {
    setUploadCallbacks({
      onStart: (fileName: string, documentType: string, options?: {
        fileSize?: number
        multiPageProps?: {
          multi_page_set_id: string
          sheet_index: string
          sheet_count: string
          multi_page_set_name: string
        }
        // FS-499: server-issued docId for pre-registered files; mirrors the
        // canonical onStart options shape in store/modalSlice.ts.
        docId?: string
      }) => {
        // Compute decreasing timestamps within a batch so DESC sort preserves selection order.
        // First file gets the base time (highest), each subsequent file gets 1ms less.
        const now = Date.now()
        if (now - lastUploadAddTime.current > 200) {
          uploadBatchBase.current = now
          uploadBatchOffset.current = 0
        }
        lastUploadAddTime.current = now
        const uploadStartedAt = new Date(
          uploadBatchBase.current - uploadBatchOffset.current++
        ).toISOString()

        // Create temporary document entry with the correct document type.
        // FS-499: when the caller has pre-registered this file via
        // /register-pending, use the server-issued docId as the temp row's
        // id. That way the temp row IS the real DB row's mirror — when
        // fetchDocuments returns the pending row, the matching effect
        // (line 440+ below) replaces the temp doc cleanly. On page refresh,
        // the temp doc is gone but the pending row is still in Postgres.
        const tempDoc = createTempDocument(
          fileName,
          documentType as DocumentRecord['type']
        )
        if (options?.docId) {
          tempDoc.id = options.docId
        }
        tempDoc.upload_started_at = uploadStartedAt

        // Apply file size so the folder shows correct total immediately
        if (options?.fileSize) {
          tempDoc.file_size = options.fileSize
        }

        // Apply multi-page properties so the temp doc is grouped into a folder immediately
        if (options?.multiPageProps) {
          tempDoc.properties = { ...tempDoc.properties, ...options.multiPageProps }
        }

        setUploadingDocuments((prev) => [...prev, tempDoc])

        // FS-489: track in-flight count so we can detect batch completion
        // in `onComplete` and run the backstop cleanup.
        uploadInFlightRef.current += 1

        // Track filename -> docId mapping and create abort controller
        uploadFileToDocIdRef.current.set(fileName, tempDoc.id)
        const controller = new AbortController()
        uploadAbortControllersRef.current.set(tempDoc.id, controller)

        return uploadStartedAt
      },
      onComplete: (fileName: string) => {
        // Update temp document status from "uploading" to "processing"
        // This ensures smooth transition with no "disappearing" file
        setUploadingDocuments((prev) =>
          prev.map((doc) =>
            doc.name === fileName ? { ...doc, status: 'processing' } : doc
          )
        )

        // Clean up controller using filename -> docId mapping
        const docId = uploadFileToDocIdRef.current.get(fileName)
        if (docId) {
          uploadFileToDocIdRef.current.delete(fileName)
          uploadAbortControllersRef.current.delete(docId)
        }

        // FS-489: this is the ONLY decrement site for `uploadInFlightRef`.
        // Every upload wrapper fires this `onComplete` in a `finally`
        // block, so success AND abort paths both reach here exactly once
        // per `onStart`. The cancel handlers (`handleDeleteDoc`,
        // `handleBulkDelete`) deliberately do NOT call
        // `releaseUploadSlot` — see the `uploadInFlightRef` docblock above
        // for why a second decrement there would double-count.
        releaseUploadSlot()
      },
      onDiscard: (docId: string) => {
        // FS-499: remove the temp row entirely. Distinct from onComplete,
        // which transitions to 'processing' and waits for the real server
        // doc to match before removing — for a discarded file no real doc
        // will ever arrive (we just called /discard-pending), so the temp
        // row must be cleared here directly. Filters by docId (not name)
        // so duplicate filenames across batches don't collide.
        setUploadingDocuments((prev) => prev.filter((doc) => doc.id !== docId))
        for (const [fileName, mappedId] of uploadFileToDocIdRef.current.entries()) {
          if (mappedId === docId) {
            uploadFileToDocIdRef.current.delete(fileName)
            break
          }
        }
        uploadAbortControllersRef.current.delete(docId)
        releaseUploadSlot()
      },
      getAbortSignal: (fileName: string) => {
        // Get the abort signal for this file from the controller ref (synchronous access)
        const docId = uploadFileToDocIdRef.current.get(fileName)
        if (docId) {
          const controller = uploadAbortControllersRef.current.get(docId)
          return controller?.signal
        }
        return undefined
      },
    })

    // Cleanup: remove callbacks when component unmounts
    return () => {
      setUploadCallbacks(undefined)
    }
  }, [setUploadCallbacks, releaseUploadSlot])

  // Debounce search term to avoid API calls per keystroke
  // Page reset is co-located here to avoid double-fetch from independent timers
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm, setPage])

  // Fetch documents from server whenever pagination/filter/sort params change
  useEffect(() => {
    Promise.resolve(
      fetchDocuments({
        page_number: page - 1, // API is 0-indexed, UI is 1-indexed
        page_size: pageSize,
        sort_by: sortBy === 'created_at' ? 'upload_started_at' : sortBy,
        sort_direction: sortDir,
        search: debouncedSearch || undefined,
        type: selectedType !== 'all' ? selectedType : undefined,
        user_id: selectedUploader !== 'all' ? selectedUploader : undefined,
      })
    ).finally(() => setInitialLoadComplete(true))
  }, [
    page,
    pageSize,
    sortBy,
    sortDir,
    debouncedSearch,
    selectedType,
    selectedUploader,
    fetchDocuments,
  ])

  // Fetch organization users into the store on mount.
  // Always fetch (not gated on length) so a prior failed call is retried.
  useEffect(() => {
    fetchOrganizationUsers()
  }, [fetchOrganizationUsers])

  // Merge temp uploading documents with server-returned documents (server handles filtering/pagination)
  const filteredData = useMemo(() => {
    // Merge ALL temp and real documents first
    const allDocuments = [...uploadingDocuments, ...documents].map((doc) => ({
      ...doc,
      // Preserve existing uploader_name (from temp docs or API), fall back to userIdToNameMap
      uploader_name:
        doc.uploader_name || userIdToNameMap.get(doc.user_id) || null,
    }))

    // Deduplicate by (name, type) so the same file can exist as different types (e.g. document vs timeseries).
    // Also treat real doc as same as temp when backend auto-renamed (e.g. "file (1).xlsx") so we show one row.
    // Policy: real > stale temp; in-progress temp > old real (so re-uploads show progress/cancel); same type: prefer NEWEST.
    const nameTypeKey = (doc: (typeof allDocuments)[number]) =>
      `${doc.name}\0${doc.type}`
    const docsMatch = (
      a: (typeof allDocuments)[number],
      b: (typeof allDocuments)[number]
    ) => {
      const aIsTemp = uploadingDocuments.some((t) => t.id === a.id)
      const bIsTemp = uploadingDocuments.some((t) => t.id === b.id)
      // When one is temp and one is real, use realDocMatchesTemp with correct argument
      // order (real first, temp second) so the temporal check works properly.
      if (aIsTemp !== bIsTemp) {
        const real = aIsTemp ? b : a
        const temp = aIsTemp ? a : b
        return realDocMatchesTemp(real, temp)
      }
      // Both real or both temp: match by exact name+type
      if (nameTypeKey(a) === nameTypeKey(b)) return true
      return false
    }
    const uniqueDocuments: typeof allDocuments = []
    allDocuments.forEach((doc) => {
      const matchIdx = uniqueDocuments.findIndex((existing) =>
        docsMatch(existing, doc)
      )
      if (matchIdx >= 0) {
        const existing = uniqueDocuments[matchIdx]
        const isExistingTemp = uploadingDocuments.some(
          (t) => t.id === existing.id
        )
        const isCurrentTemp = uploadingDocuments.some((t) => t.id === doc.id)
        const shouldReplace =
          isExistingTemp && !isCurrentTemp
            ? true // real wins over stale temp
            : !isExistingTemp && isCurrentTemp
            ? true // in-progress temp wins over old real (re-upload shows progress/cancel)
            : new Date(doc.created_at) > new Date(existing.created_at) // both same type: prefer newer
        if (shouldReplace) uniqueDocuments[matchIdx] = doc
      } else {
        uniqueDocuments.push(doc)
      }
    })

    // Apply client-side sort so uploading temp docs respect the active sort order
    // (without this, temp docs are always pinned at the top of the merged array)
    const effectiveSortBy =
      sortBy === 'created_at' ? 'upload_started_at' : sortBy
    uniqueDocuments.sort((a, b) => {
      let cmp = 0
      switch (effectiveSortBy) {
        case 'name':
          cmp = a.name.localeCompare(b.name)
          break
        case 'type':
          cmp = (a.type || '').localeCompare(b.type || '')
          break
        case 'status':
          cmp = (a.status || '').localeCompare(b.status || '')
          break
        case 'uploaded_by':
          cmp = (a.uploader_name || '').localeCompare(b.uploader_name || '')
          break
        case 'upload_started_at':
        default: {
          const aTime = new Date(a.upload_started_at || a.created_at).getTime()
          const bTime = new Date(b.upload_started_at || b.created_at).getTime()
          cmp = aTime - bTime
          break
        }
      }
      return sortDir === 'desc' ? -cmp : cmp
    })

    return uniqueDocuments
  }, [
    documents,
    uploadingDocuments,
    userIdToNameMap,
    realDocMatchesTemp,
    sortBy,
    sortDir,
  ])

  // Get all active organization users for the uploader filter dropdown
  const uniqueUploaders = useMemo(() => {
    return organizationUsers
      .filter((user) => user.user_id && user.status === 'active')
      .map(
        (user) =>
          [user.user_id!, user.name || user.email || user.user_id!] as [
            string,
            string,
          ]
      )
      .sort((a, b) => a[1].localeCompare(b[1]))
  }, [organizationUsers])

  // Group multi-page sets and prepare table data
  const processedTableData = useMemo(() => {
    // Collect set members, preserving filteredData order
    const setGroups = new Map<string, DocumentRecord[]>()
    filteredData.forEach((doc) => {
      const setId = doc.properties.multi_page_set_id
      if (setId && (doc.type === 'pnid' || doc.type === 'pfd')) {
        if (!setGroups.has(setId)) setGroups.set(setId, [])
        setGroups.get(setId)!.push(doc)
      }
    })

    // Determine which set IDs actually form groups (>1 doc)
    const groupSetIds = new Set<string>()
    setGroups.forEach((docs, setId) => {
      if (docs.length > 1) groupSetIds.add(setId)
    })

    // Walk filteredData in order, emitting groups at first-member position.
    // Discriminated union matching CatalogTable's ProcessedEntry so this can be
    // passed straight to its `orderedEntries` prop.
    const emittedSets = new Set<string>()
    const result: Array<
      | { type: 'group'; data: DocumentRecord[]; setId: string }
      | { type: 'individual'; data: DocumentRecord }
    > = []

    filteredData.forEach((doc) => {
      const setId = doc.properties.multi_page_set_id
      if (setId && groupSetIds.has(setId)) {
        // Multi-page group: emit once at position of first member
        if (!emittedSets.has(setId)) {
          emittedSets.add(setId)
          result.push({ type: 'group', data: setGroups.get(setId)!, setId })
        }
      } else {
        result.push({ type: 'individual', data: doc })
      }
    })

    return result
  }, [filteredData])

  // Clean up invalid selections when filtered data changes (separate effect to avoid circular dependency)
  useEffect(() => {
    if (selectedDocIds.size > 0) {
      const validIds = new Set<string>()
      processedTableData.forEach((item) => {
        if (item.type === 'individual') {
          validIds.add((item.data as DocumentRecord).id)
        } else {
          ;(item.data as DocumentRecord[]).forEach((doc) => validIds.add(doc.id))
        }
      })
      const updatedSelections = new Set(
        Array.from(selectedDocIds).filter((id) => validIds.has(id))
      )
      // Only update if selections actually changed to avoid unnecessary re-renders
      if (updatedSelections.size !== selectedDocIds.size) {
        setSelectedDocIds(updatedSelections)
      }
    }
  }, [processedTableData]) // Only depends on processedTableData, not selectedDocIds (avoids circular dependency)

  // Separate grouped and individual documents for pagination
  const groupedEntries = useMemo(() => {
    return processedTableData.filter((item) => item.type === 'group') as Array<{
      type: 'group'
      data: DocumentRecord[]
      setId: string
    }>
  }, [processedTableData])

  const individualEntries = useMemo(() => {
    return processedTableData
      .filter((item) => item.type === 'individual')
      .map((item) => item.data as DocumentRecord)
  }, [processedTableData])

  // Server already returns the correct page of data — just use it directly
  // and calculate totalPages from the server-provided totalDocuments count
  useEffect(() => {
    const total = Math.ceil(totalDocuments / pageSize) || 1
    setTotalPages(total)
    setTablePage(individualEntries)
    setGroupedPageEntries(groupedEntries)

    // Clamp page if it exceeds valid range
    if (page > total && total > 0) {
      setPage(total)
    }
  }, [
    groupedEntries,
    individualEntries,
    pageSize,
    page,
    setPage,
    totalDocuments,
  ])

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current)
      }
    }
  }, [])

  const handleTableSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)

    // Debounce analytics tracking (page reset handled by debouncedSearch effect)
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current)
    }
    searchDebounceRef.current = setTimeout(() => {
      if (value.trim()) {
        trackEvent('document-searched', {
          query_length: value.trim().length,
        })
      }
    }, 300)
  }

  const handleTypeChange = (type: string) => {
    setSelectedType(type)
    trackEvent('document-filtered', { filter_type: 'type', filter_value: type })
    setPage(1)
  }

  const handleUploaderChange = (uploader: string) => {
    setSelectedUploader(uploader)
    trackEvent('document-filtered', {
      filter_type: 'uploader',
      filter_value: uploader,
    })
    setPage(1)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (newSize: number) => {
    // Calculate new page to maintain approximate position
    const currentPosition = (page - 1) * pageSize
    const newTotalPages = Math.ceil(totalDocuments / newSize)
    const newPage = Math.max(
      1,
      Math.min(Math.floor(currentPosition / newSize) + 1, newTotalPages || 1)
    )

    setPageSize(newSize)
    setPage(newPage)
  }

  const handleSortChange = (newSorting: typeof DEFAULT_SORT) => {
    if (newSorting.length > 0) {
      const sort = newSorting[0]
      setSortBy(sort.id)
      setSortDir(sort.desc ? 'desc' : 'asc')
    } else {
      // No sorting, use default sorting by created_at (newest first)
      setSortBy('created_at')
      setSortDir('desc')
    }
  }

  const handleRowClick = (doc: DocumentRecord) => {
    // Prevent clicking on uploading documents
    if (doc.status === 'uploading') {
      return
    }

    // Check for failed status for ALL file types - show error toast
    if (doc.status === 'failed' || doc.status === 'error') {
      const errorMsg =
        doc.error_message ||
        t(
          'data_catalog:file_processing_failed',
          'File processing failed. Please try uploading again or contact support.'
        )
      toast.error(errorMsg)
      return
    }

    // Prevent opening files that are still processing (not indexed)
    // Skip for P&ID - they use graph_id check instead
    // Skip for spreadsheet documents with db_path - they can open in datasheet viewer before indexing completes
    const ext = doc.name?.split('.').pop()?.toLowerCase()
    const isSpreadsheetWithDb =
      doc.type === 'document' &&
      ['xlsx', 'xls', 'csv'].includes(ext || '') &&
      doc.properties?.db_path
    // For multi-page P&IDs (has pnid_graph_ids), require is_indexed before opening.
    // pnid_graph_ids is a runtime-only property not yet modeled in DocumentRecord.
    const isMultiPagePnid =
      doc.type === 'pnid' &&
      Boolean((doc.properties as { pnid_graph_ids?: unknown } | undefined)?.pnid_graph_ids)
    // A live_stream anchor (Eventhouse / streamed tags) is deliberately
    // is_indexed=false — there is no file to index; its metadata + tags come
    // from the harvest. Gating on is_indexed made double-click dead-end on
    // "File is still being processed..." forever.
    const isLiveStream = doc.type === 'live_stream'
    // A video brief is generated, never indexed; its readiness is `status`.
    const isVideo = doc.type === DOC_TYPES.video
    if (
      (doc.type !== 'pnid' || isMultiPagePnid) &&
      !isSpreadsheetWithDb &&
      !isLiveStream &&
      !isVideo &&
      !doc.is_indexed
    ) {
      toast.info(
        t(
          'data_catalog:file_still_processing',
          'File is still being processed...'
        )
      )
      return
    }

    const { pnid, pfd, document, table, timeseries, interview, transcript, video } = DOC_TYPES
    switch (doc.type) {
      case pnid: {
        // Multi-page PDF: document has pnid_graph_ids map (page→graph_id).
        // pnid_graph_ids is a runtime-only property not yet modeled in DocumentRecord.
        const rawGraphIds = (doc.properties as { pnid_graph_ids?: unknown }).pnid_graph_ids
        let pnidGraphIds: Record<string, string> | undefined
        try {
          pnidGraphIds =
            typeof rawGraphIds === 'string' ? JSON.parse(rawGraphIds) :
            typeof rawGraphIds === 'object' && rawGraphIds ? rawGraphIds as Record<string, string> :
            undefined
        } catch {
          pnidGraphIds = undefined
        }

        if (pnidGraphIds && Object.keys(pnidGraphIds).length >= 1) {
          const sortedPages = Object.keys(pnidGraphIds).sort((a, b) => Number(a) - Number(b))
          const firstGraphId = pnidGraphIds[sortedPages[0]]
          if (Object.keys(pnidGraphIds).length === 1) {
            return navigate(`/graph-editor/${firstGraphId}?editor=d3&documentId=${doc.id}`)
          }
          const pnidPages = sortedPages.map(p => `${p}:${pnidGraphIds![p]}`).join(',')
          return navigate(`/graph-editor/${firstGraphId}?editor=d3&documentId=${doc.id}&pnidPages=${pnidPages}`)
        }

        if (doc.properties.graph_id) {
          return navigate(`/graph-editor/${doc.properties.graph_id}?editor=d3`)
        }
        return
      }

      case pfd:
        if (doc.properties.graph_id) {
          return navigate(`/graph-editor/${doc.properties.graph_id}?editor=d3`)
        }
        // Show user-friendly error message instead of silent failure
        toast.error(
          t(
            'data_catalog:pfd_not_ready',
            'This PFD cannot be opened. Please contact support if the issue persists.'
          )
        )
        return

      case document: {
        // xlsx/xls/csv files with type 'document' should open in the datasheet viewer
        // but only if the SQLite db has been created (db_path exists)
        if (['xlsx', 'xls', 'csv'].includes(ext || '')) {
          if (doc.properties?.db_path) {
            return navigate(`/datasheet/${doc.id}`)
          }
          toast.info(
            t(
              'data_catalog:file_still_processing',
              'File is still being processed...'
            )
          )
          return
        }
        return navigate(`/document/${doc.id}`)
      }

      case table:
        return navigate(`/datasheet/${doc.id}`)

      case timeseries:
        // Route to datasheet if db_path exists (xlsx/csv files with successful SQLite)
        if (doc.properties?.db_path) {
          return navigate(`/datasheet/${doc.id}`)
        }
        // Check if it's a JSON file - route to timeseries viewer
        if (doc.name?.toLowerCase().endsWith('.json')) {
          return navigate(`/timeseries/${doc.id}`)
        }
        // Fallback to document viewer for CSV/XLSX without db_path (failed SQLite creation)
        return navigate(`/document/${doc.id}`)
      // Live streams have no file behind them, so the .json / db_path
      // discriminators above don't apply. Its own viewer (LiveStreamPreview)
      // shows where the stream points, whether it is live, when the tag
      // inventory was last refreshed and which tags it carries — from the
      // document + connection rows only, so opening it costs no Kusto query.
      case 'live_stream':
        return navigate(`/live-stream/${doc.id}`)

      case interview:
        return navigate(`/interview/${doc.id}`)

      case video:
        // The brief is playable once the worker stamps `success`.
        if (doc.status !== 'success') {
          toast.info(
            t(
              'data_catalog:file_still_processing',
              'File is still being processed...'
            )
          )
          return
        }
        return navigate(`/video/${doc.id}`)
      case transcript:
        // FS-557 "User Artifacts" — the call-transcript viewer/editor.
        return navigate(`/user-artifact/${doc.id}`)

      default:
        return
    }
  }

  const handleDeleteDoc = async (id: string) => {
    // Check if this is a temporary uploading document
    if (isTempDocument(id)) {
      // Extract temp document
      const tempDoc = uploadingDocuments.find((doc) => doc.id === id)
      if (tempDoc) {
        // Abort the actual HTTP upload request using doc ID
        const controller = uploadAbortControllersRef.current.get(tempDoc.id)
        if (controller) {
          try {
            controller.abort()
          } catch (e) {
            console.warn('Failed to abort upload:', tempDoc.id, e)
          }

          uploadAbortControllersRef.current.delete(tempDoc.id)
        }

        setUploadingDocuments((prev) => prev.filter((doc) => doc.id !== id))

        // Clean up filename -> docId mapping
        uploadFileToDocIdRef.current.delete(tempDoc.name)

        // FS-489: do NOT decrement `uploadInFlightRef` here. The upload
        // wrapper fires `onComplete` in `finally` for both success and
        // abort paths (see modalSlice.ts:117-122, ModalProvider.tsx:127-131
        // and :262-264, UnifiedUploadModal.tsx:223-227), so the counter
        // decrement is already owned by that single site. Decrementing
        // here would double-count this retire event and fire the
        // batch-end backstop while other uploads in the batch are still
        // in flight. The wrapper's onComplete runs slightly later (when
        // the aborted POST settles), but the temp is already gone from
        // `uploadingDocuments` and `uploadFileToDocIdRef` so the wrapper's
        // decrement just retires the counter slot for this upload.

        toast.success(t('data_catalog:upload_cancelled', 'Upload cancelled'))
      }
      return
    }

    // Regular document deletion
    try {
      await deleteDocuments([id])
      trackEvent('document-deleted', {
        document_ids: [id],
        count: 1,
        is_bulk: false,
      })
      toast.success(t('data_catalog:delete_success'))
    } catch {
      toast.error(t('data_catalog:delete_error'))
    }
  }

  const handleDownload = async (docId: string) => {
    const doc = documents.find((d) => d.id === docId)
    if (!doc) return

    // Download the original file for all document types
    try {
      const blob = await apiService.downloadDocument(docId)

      // If document was converted from Office format to PDF, adjust the filename extension
      let downloadName = doc.name
      if (
        doc.location?.toLowerCase().endsWith('.pdf') &&
        !doc.name.toLowerCase().endsWith('.pdf')
      ) {
        const nameWithoutExt = doc.name.includes('.')
          ? doc.name.substring(0, doc.name.lastIndexOf('.'))
          : doc.name
        downloadName = `${nameWithoutExt}.pdf`
      }

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = downloadName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      trackEvent('document-downloaded', {
        document_id: doc.id,
        document_type: doc.type,
      })
      trackEvent('parse-result_exported', {
        document_id: doc.id,
        document_type: doc.type,
        export_format: downloadName.split('.').pop() || 'file',
      })
    } catch {
      toast.error(t('data_catalog:download_error'))
    }
  }

  const handleRetryProcessing = async (docId: string) => {
    const doc = documents.find((d) => d.id === docId)
    if (!doc) return

    try {
      await apiService.retryDocumentProcessing(docId)
      trackEvent('document-processing_retried', {
        document_id: doc.id,
        document_type: doc.type,
      })
      toast.success(t('data_catalog:retry_processing_success'))
      // Refresh the documents list to get updated status
      await fetchDocuments()
    } catch {
      toast.error(t('data_catalog:retry_processing_error'))
    }
  }

  // Files with a brief request in flight: a second click while the menu is
  // still open would create a second row and a second render.
  const videoRequestsRef = useRef<Set<string>>(new Set())
  const handleExplainAsVideo = async (docId: string) => {
    if (videoRequestsRef.current.has(docId)) return
    videoRequestsRef.current.add(docId)
    try {
      const receipt = await apiService.createVideoBrief({ documentId: docId })
      toast.success(t('data_catalog:video_brief.requested', { name: receipt.name }))
      // The brief's row is already there, processing; show it.
      await fetchDocuments()
    } catch {
      toast.error(t('data_catalog:video_brief.error'))
    } finally {
      videoRequestsRef.current.delete(docId)
    }
  }

  const handleRename = async (docId: string, newName: string) => {
    try {
      await apiService.renameDocument(docId, newName)
      invalidateDocumentCache(docId)
      const doc = documents.find((d) => d.id === docId)
      trackEvent('document-renamed', {
        document_id: docId,
        document_type: doc?.type,
      })
      // FS-563: patch just the renamed row in place rather than refetching the
      // whole catalog. A full fetchDocuments() replaces the `documents` array
      // wholesale, which flickers/reloads every row; updateDocument touches only
      // this row so the rest of the table stays put.
      updateDocument(docId, { name: newName })
      toast.success(t('data_catalog:rename_success'))
    } catch {
      toast.error(t('data_catalog:rename_error'))
    }
  }

  const handleRowSelect = (id: string, checked: string | boolean) => {
    setSelectedDocIds((prev) => {
      const newSet = new Set(prev)
      if (checked) {
        newSet.add(id)
      } else {
        newSet.delete(id)
      }
      return newSet
    })
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set<string>()
      groupedPageEntries.forEach((group) => {
        group.data.forEach((doc) => allIds.add(doc.id))
      })
      tablePage.forEach((doc) => allIds.add(doc.id))
      setSelectedDocIds(allIds)
    } else {
      setSelectedDocIds(new Set())
    }
  }

  const handleBulkDelete = async () => {
    if (selectedDocIds.size === 0) return

    const selectedIds = Array.from(selectedDocIds)

    // Separate temporary uploading documents from real documents
    const tempDocIds = selectedIds.filter((id) => isTempDocument(id))
    const realDocIds = selectedIds.filter((id) => !isTempDocument(id))

    try {
      // Cancel uploads for temporary documents
      if (tempDocIds.length > 0) {
        const tempDocsToCancel = uploadingDocuments.filter((doc) =>
          tempDocIds.includes(doc.id)
        )

        // Abort actual HTTP requests using doc IDs
        const fileNamesToRemove = tempDocsToCancel.map((doc) => doc.name)
        tempDocsToCancel.forEach((doc) => {
          const controller = uploadAbortControllersRef.current.get(doc.id)
          if (controller) {
            try {
              controller.abort()
            } catch (e) {
              console.warn('Failed to abort upload:', doc.id, e)
            }
          }
        })

        // Remove abort controllers by doc ID
        tempDocIds.forEach((docId) =>
          uploadAbortControllersRef.current.delete(docId)
        )

        // Remove temp documents
        setUploadingDocuments((prev) =>
          prev.filter((doc) => !tempDocIds.includes(doc.id))
        )

        // Clean up filename -> docId mappings
        fileNamesToRemove.forEach((fileName) =>
          uploadFileToDocIdRef.current.delete(fileName)
        )

        // FS-489: do NOT decrement `uploadInFlightRef` here. The upload
        // wrapper fires `onComplete` in `finally` for both success and
        // abort, so each cancelled upload's counter slot is retired
        // exactly once by the wrapper's onComplete. Looping a decrement
        // here would double-count and fire the batch-end backstop while
        // other uploads in the batch are still in flight.
      }

      // Delete real documents from backend
      if (realDocIds.length > 0) {
        await deleteDocuments(realDocIds)
      }

      if (realDocIds.length > 0) {
        trackEvent('document-deleted', {
          document_ids: realDocIds,
          count: realDocIds.length,
          is_bulk: realDocIds.length > 1,
        })
      }

      // Show success message
      const totalCount = selectedDocIds.size
      if (tempDocIds.length > 0 && realDocIds.length === 0) {
        toast.success(
          t(
            'data_catalog:bulk_delete.uploads_cancelled',
            `Cancelled ${tempDocIds.length} upload${
              tempDocIds.length > 1 ? 's' : ''
            }`,
            {
              count: tempDocIds.length,
            }
          )
        )
      } else {
        toast.success(t('data_catalog:bulk_delete.success', { count: totalCount }))
      }

      setSelectedDocIds(new Set())
      setShowBulkDeleteConfirm(false)
    } catch {
      toast.error(t('data_catalog:bulk_delete.error'))
    }
  }

  // Match Entity requires both sides selected: a P&ID-side doc and a timeseries doc
  // Match Entity runs tag-mapping actions, so it goes with that area.
  const tagMappingEnabled = useFeatureEnabled('tag_mapping')
  const canMatchEntity = useMemo(() => {
    if (!tagMappingEnabled) return false
    const allDocs = [...documents, ...uploadingDocuments]
    return canMatchEntitySelection(allDocs.filter((d) => selectedDocIds.has(d.id)))
  }, [tagMappingEnabled, selectedDocIds, documents, uploadingDocuments])

  // --- P&ID Grouping ---

  // Can group: 2+ P&ID/PFD docs selected, none already in a group
  const canGroupSelected = useMemo(() => {
    if (selectedDocIds.size < 2) return false
    const allDocs = [...documents, ...uploadingDocuments]
    const selectedDocs = allDocs.filter((d) => selectedDocIds.has(d.id))
    return (
      selectedDocs.length >= 2 &&
      selectedDocs.every(
        (d) =>
          (d.type === 'pnid' || d.type === 'pfd') &&
          !d.properties.multi_page_set_id
      )
    )
  }, [selectedDocIds, documents, uploadingDocuments])

  const canUngroupSelected = useMemo(() => {
    if (selectedDocIds.size === 0) return false
    const allDocs = [...documents, ...uploadingDocuments]
    const selectedDocs = allDocs.filter((d) => selectedDocIds.has(d.id))
    return (
      selectedDocs.length > 0 &&
      selectedDocs.some((d) => d.properties.multi_page_set_id)
    )
  }, [selectedDocIds, documents, uploadingDocuments])

  const selectedDocsForGroup = useMemo(() => {
    const allDocs = [...documents, ...uploadingDocuments]
    return allDocs.filter((d) => selectedDocIds.has(d.id))
  }, [selectedDocIds, documents, uploadingDocuments])

  const availableUngroupedPnids = useMemo(() => {
    const allDocs = [...documents, ...uploadingDocuments]
    return allDocs.filter(
      (d) =>
        d.type === 'pnid' &&
        !d.properties.multi_page_set_id &&
        !selectedDocIds.has(d.id)
    )
  }, [selectedDocIds, documents, uploadingDocuments])

  const availableDocsForManage = useMemo(() => {
    const allDocs = [...documents, ...uploadingDocuments]
    const currentGroupIds = new Set(manageFilesDocs.map((d) => d.id))
    return allDocs.filter(
      (d) =>
        d.type === 'pnid' &&
        !d.properties.multi_page_set_id &&
        !currentGroupIds.has(d.id)
    )
  }, [manageFilesDocs, documents, uploadingDocuments])

  const handleGroupNameNext = (name: string) => {
    setPendingGroupName(name)
    setShowGroupDialog(false)
    setTimeout(() => setShowReviewGroupModal(true), 0)
  }

  const handleCreateGroup = async (orderedDocIds: string[]) => {
    const setId = crypto.randomUUID()
    try {
      await Promise.all(
        orderedDocIds.map((docId, index) =>
          apiService.updateDocumentMetadata(docId, {
            multi_page_set_id: setId,
            sheet_index: String(index),
            multi_page_set_name: pendingGroupName,
          })
        )
      )
      await fetchDocuments()
      setSelectedDocIds(new Set())
      setShowReviewGroupModal(false)
      toast.success(t('data_catalog:graph_editor.multi_page_set.group_success'))
    } catch {
      toast.error(t('data_catalog:graph_editor.multi_page_set.group_error'))
    }
  }

  const handleRenameGroup = async (setId: string, newName: string) => {
    const groupDocs = documents.filter(
      (d) => d.properties.multi_page_set_id === setId
    )
    try {
      await Promise.all(
        groupDocs.map((doc) =>
          apiService.updateDocumentMetadata(doc.id, {
            multi_page_set_name: newName,
          })
        )
      )
      await fetchDocuments()
      toast.success(
        t('data_catalog:graph_editor.multi_page_set.rename_success')
      )
    } catch {
      toast.error(t('data_catalog:graph_editor.multi_page_set.rename_error'))
    }
  }

  const handleDeleteGroup = async (_setId: string, docs: DocumentRecord[]) => {
    try {
      await Promise.all(
        docs.map((doc) =>
          apiService.updateDocumentMetadata(doc.id, {}, [
            'multi_page_set_id',
            'sheet_index',
            'multi_page_set_name',
          ])
        )
      )
      await fetchDocuments()
      toast.success(
        t('data_catalog:graph_editor.multi_page_set.ungroup_success')
      )
    } catch {
      toast.error(t('data_catalog:graph_editor.multi_page_set.ungroup_error'))
    }
  }

  const handleUngroupSelected = async () => {
    const allDocs = [...documents, ...uploadingDocuments]
    const selectedGroupedDocs = allDocs.filter(
      (d) => selectedDocIds.has(d.id) && d.properties.multi_page_set_id
    )
    try {
      await Promise.all(
        selectedGroupedDocs.map((doc) =>
          apiService.updateDocumentMetadata(doc.id, {}, [
            'multi_page_set_id',
            'sheet_index',
            'multi_page_set_name',
          ])
        )
      )
      await fetchDocuments()
      setSelectedDocIds(new Set())
      toast.success(
        t('data_catalog:graph_editor.multi_page_set.ungroup_success')
      )
    } catch {
      toast.error(t('data_catalog:graph_editor.multi_page_set.ungroup_error'))
    }
  }

  const handleManageFiles = (setId: string, docs: DocumentRecord[]) => {
    setManageFilesSetId(setId)
    setManageFilesDocs(docs)
    setShowManageFilesModal(true)
  }

  const handleManageFilesConfirm = async (reorderedDocIds: string[]) => {
    try {
      // Determine which docs are new (not in original group) and need full metadata
      const originalDocIds = new Set(manageFilesDocs.map((d) => d.id))
      const groupName = manageFilesDocs[0]?.properties.multi_page_set_name || ''

      await Promise.all(
        reorderedDocIds.map((docId, index) => {
          const isNew = !originalDocIds.has(docId)
          return apiService.updateDocumentMetadata(docId, {
            sheet_index: String(index),
            ...(isNew
              ? {
                  multi_page_set_id: manageFilesSetId,
                  multi_page_set_name: groupName,
                }
              : {}),
          })
        })
      )
      await fetchDocuments()
      toast.success(
        t('data_catalog:graph_editor.multi_page_set.reorder_success')
      )
    } catch {
      toast.error(t('data_catalog:graph_editor.multi_page_set.reorder_error'))
    } finally {
      setShowManageFilesModal(false)
    }
  }

  const resetBehavior = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const menuActions: RowAction[] = [
    // Download action
    {
      name: t('data_catalog:row_actions.download'),
      icon: <Download className="w-4 h-4" />,
      action: (
        e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
        id: string
      ) => {
        e.stopPropagation()
        handleDownload(id)
      },
      condition: (doc: DocumentRecord) => doc.status !== 'uploading',
    },
    // Retry processing action - only shows for failed documents
    {
      name: t('data_catalog:row_actions.retry_processing'),
      icon: <RefreshCw className="w-4 h-4" />,
      action: (
        e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
        id: string
      ) => {
        e.stopPropagation()
        handleRetryProcessing(id)
      },
      // FS-621: also offer retry for the reconciler's 'stalled' state.
      condition: (doc: DocumentRecord) => doc.status === 'failed' || doc.status === 'stalled',
    },
    // Rename action
    {
      name: t('data_catalog:row_actions.rename'),
      icon: <Pencil className="w-4 h-4" />,
      isRename: true,
      action: () => {
        // Action is handled by RenameDialog via onRename callback
      },
      condition: (doc: DocumentRecord) => doc.status !== 'uploading',
    },
    // "Explain as video" after Rename, before Delete (PRD placement); only while
    // the flag is on, and the server gates it too.
    ...(explainVideoEnabled
      ? [
          {
            name: t('data_catalog:row_actions.explain_as_video'),
            icon: <MonitorPlay className="w-4 h-4" />,
            action: (
              e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
              id: string
            ) => {
              e.stopPropagation()
              handleExplainAsVideo(id)
            },
            condition: canExplainAsVideo,
          } satisfies RowAction,
        ]
      : []),
    // TODO: Remove temporary D3 menu option
    {
      name: t('data_catalog:row_actions.open_in_tl_draw'),
      file_type: [DOC_TYPES.graph],
      icon: <Pen className="w-4 h-4" />,
      action: (
        e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
        id: string
      ) => {
        e.stopPropagation()
        navigate(`/graph-editor/${id}`)
      },
      condition: (doc: DocumentRecord) => doc.status !== 'uploading',
    },
    {
      name: t('data_catalog:row_actions.delete'),
      icon: <Trash2Icon className="w-4 h-4" />,
      action: (
        e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
        id: string
      ) => {
        e.stopPropagation()
        handleDeleteDoc(id)
      },
    },
  ]

  const PageTools = () => (
    <div className="flex gap-4 items-center">
      {selectedDocIds.size > 0 && (
        <>
          <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
            {selectedDocIds.size} {selectedDocIds.size === 1 ? 'file' : 'files'}{' '}
            selected
          </span>
          {canGroupSelected && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowGroupDialog(true)}
              className="flex items-center gap-2"
            >
              <Link2 className="w-4 h-4" />
              {t('data_catalog:graph_editor.multi_page_set.group_pids')}
            </Button>
          )}
          {canUngroupSelected && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleUngroupSelected}
              className="flex items-center gap-2"
            >
              <Unlink2 className="w-4 h-4" />
              {t('data_catalog:graph_editor.multi_page_set.ungroup_selected')}
            </Button>
          )}
          {canMatchEntity && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const ids = Array.from(selectedDocIds).join(',')
                navigate(`/data-catalog/match-entity?docs=${encodeURIComponent(ids)}`)
              }}
              className="flex items-center gap-2"
            >
              <Link2 className="w-4 h-4" />
              {t('data_catalog:match_entity.button', {
                defaultValue: 'Match Entity ({{count}})',
                count: selectedDocIds.size,
              })}
            </Button>
          )}
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setShowBulkDeleteConfirm(true)}
            className="flex items-center gap-2"
          >
            <Trash2Icon className="w-4 h-4" />
            {t('data_catalog:bulk_delete.button', {
              count: selectedDocIds.size,
            })}
          </Button>
        </>
      )}
      <div className={`relative ${selectedDocIds.size > 0 ? 'w-48' : 'w-72'}`}>
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-dark-text-muted"
        />
        <Input
          placeholder={t('data_catalog:search_placeholder')}
          value={searchTerm}
          onChange={handleTableSearch}
          className="pl-9"
        />
      </div>
      <div
        className={`flex gap-3 ${
          selectedDocIds.size > 0 ? 'w-44' : 'w-60'
        } items-center`}
      >
        <Label className="flex-1">{t('data_catalog:type')}</Label>
        <Select
          value={selectedType}
          onValueChange={(value) => handleTypeChange(value)}
        >
          <SelectTrigger className="w-full">
            <SelectValue
              placeholder={t('data_catalog:filter_placeholder')}
              defaultValue={FILTER_TYPES[1]}
            />
          </SelectTrigger>
          <SelectContent>
            {FILTER_TYPES.map((type: string) => (
              <SelectItem key={type} value={type}>
                <span>{getDocTypeLabel(type)}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div
        className={`flex gap-3 ${
          selectedDocIds.size > 0 ? 'w-44' : 'w-60'
        } items-center`}
      >
        <Label className="flex-1">{t('data_catalog:uploader')}</Label>
        <Select value={selectedUploader} onValueChange={handleUploaderChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('data_catalog:uploader_filter_all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t('data_catalog:uploader_filter_all')}
            </SelectItem>
            {uniqueUploaders.map(([userId, name]) => (
              <SelectItem key={userId} value={userId}>
                <span>{name}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )

  return (
    <PageLayout title={t('data_catalog:page_title')} pageTools={PageTools}>
      <TooltipProvider>
        {isUploadEnabled && (
          <div
            className="w-full min-h-[120px] flex flex-col justify-center items-center border-2 border-dashed transition-all rounded-lg my-2 py-2 px-4 flex-shrink-0 relative cursor-pointer bg-gray-50 dark:bg-background"
            style={{
              borderColor: colors.purple[500],
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.borderColor = colors.purple[600])
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.borderColor = colors.purple[500])
            }
            onDragEnter={resetBehavior}
            onDragLeave={resetBehavior}
            onDragOver={resetBehavior}
            onDrop={(e: DragEvent<HTMLDivElement>) => {
              e.preventDefault()
              e.stopPropagation()
              const files = e.dataTransfer.files
              if (files && files.length > 0) {
                setModalData(MODAL_TYPE.uploadFile, {
                  initialFiles: Array.from(files),
                })
              }
              openModal(MODAL_TYPE.uploadFile)
            }}
            role="button"
            tabIndex={0}
            onClick={() => {
              setModalData(MODAL_TYPE.uploadFile, { initialFiles: undefined })
              openModal(MODAL_TYPE.uploadFile)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setModalData(MODAL_TYPE.uploadFile, { initialFiles: undefined })
                openModal(MODAL_TYPE.uploadFile)
              }
            }}
          >
            <div className="flex flex-col gap-2 items-center pointer-events-none">
              <Upload
                className="w-10 h-10"
                style={{ color: colors.purple[700] }}
              />
              <p className="text-base">
                <span
                  className="font-semibold"
                  style={{ color: colors.purple[700] }}
                >
                  {t('data_catalog:upload_click')}
                </span>{' '}
                {t('data_catalog:upload_from_computer')}
              </p>
            </div>
          </div>
        )}
        <div className="border rounded-lg flex-1 min-h-0 overflow-hidden">
          <div className="h-full overflow-auto">
            <CatalogTable
              cols={CATALOG_COLS}
              groupedEntries={groupedPageEntries}
              individualData={tablePage}
              orderedEntries={processedTableData}
              onRowClick={handleRowClick}
              rowActions={menuActions}
              defaultSort={sorting}
              onSortChange={handleSortChange}
              selectable={true}
              selectedRows={Array.from(selectedDocIds)}
              onRowSelect={handleRowSelect}
              onSelectAll={handleSelectAll}
              onRename={handleRename}
              loading={loading}
              showEmptyMessage={
                initialLoadComplete &&
                // Show "No Documents Found" when store is truly empty
                (documents.length === 0 ||
                  // Show "No matching files" when there's an active filter and no results
                  (groupedPageEntries.length === 0 &&
                    tablePage.length === 0 &&
                    (selectedType !== 'all' ||
                      searchTerm.trim() !== '' ||
                      selectedUploader !== 'all')))
              }
              emptyMessage={
                // Check if any filters are active to pick the right message
                selectedType !== 'all' ||
                searchTerm.trim() !== '' ||
                selectedUploader !== 'all'
                  ? t('data_catalog:no_matching_documents')
                  : t('data_catalog:no_documents')
              }
              onRenameGroup={handleRenameGroup}
              onManageFiles={handleManageFiles}
              onDeleteGroup={handleDeleteGroup}
              onOpenSet={(docs) => {
                // Find the first document with a graph_id (graph might not be ready for all sheets yet)
                const docWithGraph = docs.find((d) => d.properties.graph_id)
                if (docWithGraph && docWithGraph.properties.graph_id) {
                  // Pass set information via URL params for sheet navigation
                  const setId = docWithGraph.properties.multi_page_set_id
                  const docIds = docs.map((d) => d.id).join(',')
                  navigate(
                    `/graph-editor/${docWithGraph.properties.graph_id}?editor=d3&multiPageSet=${setId}&docIds=${docIds}&documentId=${docWithGraph.id}`
                  )
                } else {
                  // No graph ready yet - show message
                  toast.info(
                    t(
                      'data_catalog:graph_not_ready',
                      'Graph is still being processed. Please wait a moment and try again.'
                    )
                  )
                }
              }}
            />
          </div>
        </div>
        <Paginator
          currentPage={page}
          totalPages={totalPages}
          pageSize={pageSize}
          setPage={handlePageChange}
          setPageSize={handlePageSizeChange}
          totalItems={totalDocuments}
        />

        <DocumentPreviewModal
          isOpen={showPreviewModal}
          onClose={() => {
            setShowPreviewModal(false)
            setPreviewDocument(null)
          }}
          document={previewDocument}
        />

        {showBulkDeleteConfirm && (
          <AlertDialog
            open={showBulkDeleteConfirm}
            onOpenChange={setShowBulkDeleteConfirm}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t('data_catalog:bulk_delete.title', { count: selectedDocIds.size })}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t('data_catalog:bulk_delete.description')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  onClick={() => setShowBulkDeleteConfirm(false)}
                >
                  {t('common:buttons.cancel')}
                </AlertDialogCancel>
                <AlertDialogAction onClick={handleBulkDelete}>
                  {t('common:buttons.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        <GroupNameDialog
          isOpen={showGroupDialog}
          onClose={() => setShowGroupDialog(false)}
          onConfirm={handleGroupNameNext}
          confirmLabel={t('common:buttons.next', 'Next')}
        />

        {showReviewGroupModal && (
          <ReviewGroupModal
            isOpen={showReviewGroupModal}
            onClose={() => setShowReviewGroupModal(false)}
            documents={selectedDocsForGroup}
            availableDocuments={availableUngroupedPnids}
            onConfirm={handleCreateGroup}
          />
        )}

        {showManageFilesModal && (
          <ManageGroupFilesModal
            isOpen={showManageFilesModal}
            onClose={() => setShowManageFilesModal(false)}
            documents={manageFilesDocs}
            availableDocuments={availableDocsForManage}
            onConfirm={handleManageFilesConfirm}
          />
        )}
      </TooltipProvider>
    </PageLayout>
  )
}

export default DataCatalog
